import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import Assets from "../../../core/assets";
import AddScreenModal from "./screenModal/AddScreenModal";
import ConfirmActionModal from "./screenModal/ConfirmActionModal";
import UpgradeModal from "./screenModal/UpgradeModal";
import ComparePlansModal from "./screenModal/ComparePlansModal";
import {
  useGetSubscriptionsSummaryQuery,
  useGetScreensQuery,
  useDowngradeScreenMutation,
  useUpgradeScreenMutation,
  useCancelScreenMutation,
} from "../../../services";
import { ScreenRow, ScreenData } from "../../../types";
import { ScreenType } from "../../../types/subscription/subscriptionScreen/subscriptionScreen.type";

const ScreenList: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showAddScreenModal, setShowAddScreenModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState<ScreenRow | null>(null);
  const [showComparePlansModal, setShowComparePlansModal] = useState(false);

  const { data: summaryResp, isLoading: summaryLoading } =
    useGetSubscriptionsSummaryQuery(undefined);
  const { data: screensData, isLoading: screensLoading } =
    useGetScreensQuery(undefined);
  const [cancelScreen, { isLoading: isCancelling }] = useCancelScreenMutation();
  const [upgradeScreen, { isLoading: isUpgrading }] =
    useUpgradeScreenMutation();
  const [downgradeScreen, { isLoading: isDowngrading }] =
    useDowngradeScreenMutation();

  const summaryData = Array.isArray(summaryResp?.results)
    ? summaryResp.results.map((item: any) => ({
        id: item._id,
        totalScreens: item.totalProducts,
        premium: item.products
          ?.filter((p: any) => p.type === ScreenType.PREMIUM)
          .reduce((acc: any, p: any) => acc + (p.quantity || 0), 0),
        basic: item.products
          ?.filter((p: any) => p.type === ScreenType.BASIC)
          .reduce((acc: any, p: any) => acc + (p.quantity || 0), 0),
        organisation: item.organisation,
        billingDate: item.currentBilledAt
          ? new Date(item.currentBilledAt).toLocaleDateString()
          : "",
        endDate: item.endDate
          ? new Date(item.endDate).toLocaleDateString()
          : null,
        total: item.currentBilledAmount,
        status: item.status,
      }))
    : [];

  const screens: ScreenData[] = Array.isArray(screensData?.results)
    ? screensData.results.map((item: any) => ({
        _id: item._id,
        code: item.code,
        createdAt: item.createdAt,
        isDeleted: item.isDeleted,
        organisation: item.organisation,
        plan: item.plan,
        status: item.status,
        subscriber: item.subscriber,
        subscription: item.subscription,
        updatedAt: item.updatedAt,
        downgrade: item.isDowngrade,
        cancel: item.isCancelled,
        cancelledAt: item.cancelledAt,
        downgradedAt: item.downgradedAt,
        endDate: item.endDate,
        isDowngradeRequested: item.isDowngradeRequested,
      }))
    : [];
  const [deleteTarget, setDeleteTarget] = useState<ScreenRow | null>(null);
  const openDelete = (row: ScreenRow) => setDeleteTarget(row);
  const closeDelete = () => setDeleteTarget(null);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const screenId = deleteTarget.screenId;
    if (!screenId) return;
    try {
      await cancelScreen({ screenId }).unwrap();
      // Optional: Add success notification
      console.log("Screen cancelled successfully!");
    } catch (e) {
      console.error("Failed to cancel screen:", e);
    }
    closeDelete();
  };

  const openAddScreenModal = () => setShowAddScreenModal(true);
  const closeAddScreenModal = () => {
    setShowAddScreenModal(false);
  };

  // Add success handler for when screen is added successfully
  const handleScreenAdded = () => {
    closeAddScreenModal();
    // Optional: Add toast notification here
    console.log("Screen added successfully!");
  };

  const openDowngradeModal = (screen: ScreenRow) => {
    setSelectedScreen(screen);
    setShowDowngradeModal(true);
  };
  const closeDowngradeModal = () => {
    setSelectedScreen(null);
    setShowDowngradeModal(false);
  };
  const closeUpgradeModal = () => {
    setSelectedScreen(null);
    setShowUpgradeModal(false);
  };

  const openUpgradeModal = (screen: ScreenRow) => {
    setSelectedScreen(screen);
    setShowUpgradeModal(true);
  };

  const openComparePlansModal = () => setShowComparePlansModal(true);
  const closeComparePlansModal = () => setShowComparePlansModal(false);

  const confirmDowngrade = async () => {
    if (!selectedScreen) return;
    const screenId = selectedScreen.screenId; // use as string
    // prefer targetPlanId if provided (we compute it when opening modal), else fallback to planId
    const planId =
      (selectedScreen as any).targetPlanId ?? (selectedScreen as any).planId;
    if (!screenId || !planId) return;
    try {
      await downgradeScreen({ screenId, planId }).unwrap();
      console.log("Screen downgraded successfully!");
    } catch (e) {
      console.error("Failed to downgrade screen:", e);
    }
    closeDowngradeModal();
    closeUpgradeModal();
  };

  const confirmUpgrade = async () => {
    if (!selectedScreen) return;
    const screenId = selectedScreen.screenId;
    // prefer targetPlanId if provided (we compute it when opening modal), else fallback to planId
    const planId =
      (selectedScreen as any).targetPlanId ?? (selectedScreen as any).planId;
    if (!screenId || !planId) return;
    try {
      await upgradeScreen({ screenId, planId }).unwrap();
      console.log("Screen upgraded successfully!");
    } catch (e) {
      console.error("Failed to upgrade screen:", e);
    }
    closeDowngradeModal();
    closeUpgradeModal();
  };

  const toggleRow = (rowId: string) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState<number>(PAGE_SIZE);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  React.useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [summaryData.length]);

  // Slice visible summaries
  const visibleSummaries = React.useMemo(
    () => summaryData.slice(0, visibleCount),
    [summaryData, visibleCount]
  );

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    const hasMore = visibleCount < summaryData.length;
    if (nearBottom && hasMore && !isLoading) {
      setIsLoading(true);
      // Yield to frame for smoother UI
      requestAnimationFrame(() => {
        setVisibleCount((prev) =>
          Math.min(prev + PAGE_SIZE, summaryData.length)
        );
        setIsLoading(false);
      });
    }
  };

  // THEME CLASSES
  const pageBg = theme === "dark" ? "bg-[#0A0A0A]" : "bg-gray-50";
  const heading = "text-text";
  const crumbLink = theme === "dark" ? "text-gray-500" : "text-gray-500";
  const crumbSep = theme === "dark" ? "text-gray-600" : "text-gray-400";

  const cardClass =
    theme === "dark"
      ? "bg-[#1F1F1F]  border border-gray-800 text-gray-100"
      : "bg-white border border-gray-200 text-gray-900";

  const cardHeader =
    theme === "dark"
      ? "px-6 py-4 border-b border-[#2A2A2A] text-white flex items-center justify-between"
      : "px-6 py-4 border-b border-gray-200 text-gray-800 flex items-center justify-between";

  const thClass =
    theme === "dark"
      ? "px-6 py-3 text-left text-sm font-bold text-[#F3F3F6]  tracking-wider border-b border-[#2A2A2A]"
      : "px-6 py-3 text-left text-sm font-bold text-gray-700  tracking-wider border-b border-gray-200  ";

  const tdClass =
    theme === "dark"
      ? "px-6 py-4 text-sm text-text-table border-b border-[#2A2A2A]"
      : "px-6 py-4 text-sm text-text-table border-b border-gray-200";

  const iconHover = theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5";

  const UpgradeIcon: React.FC<{ theme: "light" | "dark" }> = ({ theme }) => {
    const box = theme === "light" ? "#7A8618" : "#C6D92C";
    const arrow = theme === "light" ? "#7A8618" : "#C6D92C";
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M2.5 12C2.5 16.4783 2.5 18.7175 3.89124 20.1088C5.28249 21.5 7.52166 21.5 12 21.5C16.4783 21.5 18.7175 21.5 20.1088 20.1088C21.5 18.7175 21.5 16.4783 21.5 12C21.5 7.5217 21.5 5.2825 20.1088 3.8912C18.7175 2.5 16.4783 2.5 12 2.5C7.52166 2.5 5.28249 2.5 3.89124 3.8912C2.5 5.2825 2.5 7.5217 2.5 12Z"
          stroke={box}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16 11.5C16 11.5 13.0541 7.5 12 7.5C10.9459 7.5 8 11.5 8 11.5M12 8V16.5"
          stroke={arrow}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const DowngradeIcon: React.FC<{ theme: "light" | "dark" }> = ({ theme }) => {
    const box = theme === "light" ? "#768217" : "#C6D92C";
    const arrow = theme === "light" ? "#7A8618" : "#C6D92C";

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M2.5 12C2.5 7.52166 2.5 5.28249 3.89124 3.89124C5.28249 2.5 7.52166 2.5 12 2.5C16.4783 2.5 18.7175 2.5 20.1088 3.89124C21.5 5.28249 21.5 7.52166 21.5 12C21.5 16.4783 21.5 18.7175 20.1088 20.1088C18.7175 21.5 16.4783 21.5 12 21.5C7.52166 21.5 5.28249 21.5 3.89124 20.1088C2.5 18.7175 2.5 16.4783 2.5 12Z"
          stroke={box}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16 12.5C16 12.5 13.0541 16.5 12 16.5C10.9459 16.5 8 12.5 8 12.5M12 16V7.5"
          stroke={arrow}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };
  const ResubscribeIcon: React.FC<{ theme: "light" | "dark" }> = ({
    theme,
  }) => {
    const box = theme === "light" ? "#768217" : "#C6D92C";
    const arrow = theme === "light" ? "#7A8618" : "#C6D92C";

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
      >
        <path
          d="M16.3884 3L17.3913 3.97574C17.8393 4.41165 18.0633 4.62961 17.9844 4.81481C17.9056 5 17.5888 5 16.9552 5H9.19422C5.22096 5 2 8.13401 2 12C2 13.4872 2.47668 14.8662 3.2895 16"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7.61156 21L6.60875 20.0243C6.16074 19.5883 5.93673 19.3704 6.01557 19.1852C6.09441 19 6.4112 19 7.04478 19H14.8058C18.779 19 22 15.866 22 12C22 10.5128 21.5233 9.13383 20.7105 8"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const headerStickyClasses =
    theme === "dark"
      ? "sticky top-0 z-10 bg-[#1E1E1E]"
      : "sticky top-0 z-10 bg-white";
  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen
  );
  const sidebarWidth = isSidebarOpen ? 256 : 64;

  const scrollColors = React.useMemo(
    () =>
      theme === "dark"
        ? { track: "#111111", thumb: "#2A2A2A" }
        : { track: "#F5F7FA", thumb: "#C9CED6" },
    [theme]
  );

  return (
    <div
      className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
    >
      <style>
        {`
          .scroll-area {
            scrollbar-width: thin; /* Firefox */
            scrollbar-color: var(--scroll-thumb) var(--scroll-track); /* Firefox */
          }
          .scroll-area::-webkit-scrollbar {
            width: 10px;
            height: 10px;
          }
          .scroll-area::-webkit-scrollbar-track {
            background: var(--scroll-track);
            border-radius: 8px;
          }
          .scroll-area::-webkit-scrollbar-thumb {
            background-color: var(--scroll-thumb);
            border-radius: 8px;
            border: 2px solid var(--scroll-track);
          }
        `}
      </style>

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className={`text-lg font-medium ${heading} mb-2`}>
            Subscription Overview
          </h1>
          <div className="text-sm flex items-center gap-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M3 11.9896V14.5C3 17.7998 3 19.4497 4.02513 20.4749C5.05025 21.5 6.70017 21.5 10 21.5H14C17.2998 21.5 18.9497 21.5 19.9749 20.4749C21 19.4497 21 17.7998 21 14.5V11.9896C21 10.3083 21 9.46773 20.6441 8.74005C20.2882 8.01237 19.6247 7.49628 18.2976 6.46411L16.2976 4.90855C14.2331 3.30285 13.2009 2.5 12 2.5C10.7991 2.5 9.76689 3.30285 7.70242 4.90855L5.70241 6.46411C4.37533 7.49628 3.71179 8.01237 3.3559 8.74005C3 9.46773 3 10.3083 3 11.9896Z"
                stroke="#ADADAD"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15 21.5V16.5C15 15.0858 15 14.3787 14.5607 13.9393C14.1213 13.5 13.4142 13.5 12 13.5C10.5858 13.5 9.87868 13.5 9.43934 13.9393C9 14.3787 9 15.0858 9 16.5V21.5"
                stroke="#ADADAD"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Link
              to="/"
              className={`${crumbLink} hover:text-[#B6CB24] transition-colors`}
            >
              Dashboard
            </Link>
            <span className={crumbSep}>&gt;</span>
            <span className="text-[#B6CB24] font-normal">Screens</span>
          </div>
        </div>
        <button
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#C6D92C] text-[#B6CB24] bg-[#B6CB24]/5 hover:bg-[#B6CB24]/10 text-sm font-medium"
          onClick={openComparePlansModal}
          style={{
            border: "1px solid var(--menu-active-border, #C6D92C)",
            background:
              "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M3 11C3 8.17157 3 6.75736 3.87868 5.87868C4.75736 5 6.17157 5 9 5H11C13.8284 5 15.2426 5 16.1213 5.87868C17 6.75736 17 8.17157 17 11V16C17 18.8284 17 20.2426 16.1213 21.1213C15.2426 22 13.8284 22 11 22H9C6.17157 22 4.75736 22 3.87868 21.1213C3 20.2426 3 18.8284 3 16V11Z"
              stroke="white"
              strokeWidth="1.5"
            />
            <path
              d="M8.04435 11.762L8.42742 11.3048C9.15621 10.4349 9.52061 10 10 10C10.4794 10 10.8438 10.4349 11.5726 11.3048L11.9556 11.762C12.6519 12.593 13 13.0086 13 13.5C13 13.9914 12.6519 14.407 11.9556 15.238L11.5726 15.6952C10.8438 16.5651 10.4794 17 10 17C9.52061 17 9.15621 16.5651 8.42742 15.6952L8.04435 15.238C7.34812 14.407 7 13.9914 7 13.5C7 13.0086 7.34812 12.593 8.04435 11.762Z"
              stroke="white"
              strokeWidth="1.5"
            />
            <path
              d="M16.9244 19C18.0202 18.3874 18.3929 17.0406 19.1383 14.3469L20.1925 10.5375C20.938 7.84378 21.3107 6.49694 20.678 5.4359C20.0453 4.37485 18.6543 4.01397 15.8724 3.2922L13.9052 2.78183C11.1232 2.06006 9.73225 1.69918 8.63642 2.31177C7.85623 2.74792 7.44258 3.55626 7 4.95786"
              stroke="white"
              strokeWidth="1.5"
            />
          </svg>{" "}
          Compare Plans
        </button>
      </div>

      {/* Main Table Card */}
      <div className={`${cardClass} rounded-lg overflow-hidden shadow-lg`}>
        <div className={cardHeader}>
          <span className="text-base text-md font-bold">
            Screen Subscription Details
          </span>
          <button
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#B6CB24] text-[#B6CB24] bg-[#B6CB24]/10 text-sm font-medium"
            onClick={openAddScreenModal}
          >
            <img
              src={
                theme === "dark"
                  ? Assets.screenAddPlaceholderDark
                  : Assets.screenAddPlaceholderLight
              }
              alt={theme === "dark" ? "Dark Mode" : "Light Mode"}
              className="w-4 h-4"
            />
            Add new screens
          </button>
        </div>

        <div className="overflow-x-auto">
          {/* CHANGED: add scroll-area class and theme CSS vars */}
          <div
            className="max-h-[60vh] overflow-y-auto custom-scrollbar scroll-area"
            onScroll={handleScroll}
            style={
              {
                ["--scroll-track" as any]: scrollColors.track,
                ["--scroll-thumb" as any]: scrollColors.thumb,
              } as React.CSSProperties
            }
          >
            <table className="min-w-full">
              <thead className={headerStickyClasses}>
                <tr>
                  <th className={thClass}>Total Screens</th>
                  <th className={`${thClass} flex items-center gap-2 `}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        cx="12.75"
                        cy="12.75"
                        r="10.75"
                        fill="url(#paint0_radial_278_5485)"
                      />
                      <g filter="url(#filter0_n_278_5485)">
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                          fill="url(#paint1_linear_278_5485)"
                        />
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                          fill="#C6D92C"
                          fillOpacity="0.1"
                        />
                      </g>
                      <circle
                        cx="12.75"
                        cy="12.75"
                        r="10.25"
                        stroke="url(#paint2_linear_278_5485)"
                        stroke-opacity="0.88"
                      />
                      <path
                        d="M11.2939 7.81641C11.6993 7.06107 12.8114 7.06112 13.2168 7.81641L14.4531 10.1191L14.5117 10.2295L14.6357 10.249L17.3369 10.6572C18.1918 10.7865 18.5864 11.8269 17.9287 12.4531L16.0176 14.2734L15.9219 14.3652L15.9434 14.4951L16.3633 17.0254C16.4394 17.4839 16.238 17.8433 15.9238 18.0518C15.6023 18.2649 15.1713 18.3136 14.8096 18.1387L12.3643 16.9561L12.2559 16.9033L12.1465 16.9561L9.70117 18.1387C9.33945 18.3135 8.90938 18.2648 8.58789 18.0518C8.27342 17.8433 8.07137 17.4841 8.14746 17.0254L8.56738 14.4951L8.58887 14.3652L8.49316 14.2734L6.58203 12.4531C5.92445 11.8269 6.31904 10.7865 7.17383 10.6572L9.875 10.249L9.99902 10.2295L10.0586 10.1191L11.2939 7.81641Z"
                        stroke="url(#paint3_linear_278_5485)"
                        stroke-opacity="0.88"
                        strokeWidth="0.5"
                      />
                      <defs>
                        <filter
                          id="filter0_n_278_5485"
                          x="2"
                          y="2"
                          width="21.5"
                          height="21.5"
                          filterUnits="userSpaceOnUse"
                          color-interpolation-filters="sRGB"
                        >
                          <feFlood
                            flood-opacity="0"
                            result="BackgroundImageFix"
                          />
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="BackgroundImageFix"
                            result="shape"
                          />
                          <feTurbulence
                            type="fractalNoise"
                            baseFrequency="2 2"
                            stitchTiles="stitch"
                            numOctaves="3"
                            result="noise"
                            seed="2744"
                          />
                          <feColorMatrix
                            in="noise"
                            type="luminanceToAlpha"
                            result="alphaNoise"
                          />
                          <feComponentTransfer
                            in="alphaNoise"
                            result="coloredNoise1"
                          >
                            <feFuncA
                              type="discrete"
                              tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
                            />
                          </feComponentTransfer>
                          <feComposite
                            operator="in"
                            in2="shape"
                            in="coloredNoise1"
                            result="noise1Clipped"
                          />
                          <feFlood
                            flood-color="rgba(0, 0, 0, 0.25)"
                            result="color1Flood"
                          />
                          <feComposite
                            operator="in"
                            in2="noise1Clipped"
                            in="color1Flood"
                            result="color1"
                          />
                          <feMerge result="effect1_noise_278_5485">
                            <feMergeNode in="shape" />
                            <feMergeNode in="color1" />
                          </feMerge>
                        </filter>
                        <radialGradient
                          id="paint0_radial_278_5485"
                          cx="0"
                          cy="0"
                          r="1"
                          gradientUnits="userSpaceOnUse"
                          gradientTransform="translate(10.5 10) rotate(54.7824) scale(10.4043)"
                        >
                          <stop stopColor="#D1E156" />
                          <stop offset="0.552885" stopColor="#C6D92C" />
                          <stop offset="1" stopColor="#7A8618" />
                        </radialGradient>
                        <linearGradient
                          id="paint1_linear_278_5485"
                          x1="12.75"
                          y1="3"
                          x2="12.75"
                          y2="22.5"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#2E2E2E" />
                          <stop offset="1" stopColor="#666666" />
                        </linearGradient>
                        <linearGradient
                          id="paint2_linear_278_5485"
                          x1="2"
                          y1="12.75"
                          x2="23.5"
                          y2="12.75"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#C6D92C" />
                          <stop offset="0.533654" stopColor="#EDF5C0" />
                          <stop offset="1" stopColor="#C6D92C" />
                        </linearGradient>
                        <linearGradient
                          id="paint3_linear_278_5485"
                          x1="6"
                          y1="12.7476"
                          x2="18.5109"
                          y2="12.7476"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#C6D92C" />
                          <stop offset="0.533654" stopColor="#EDF5C0" />
                          <stop offset="1" stopColor="#C6D92C" />
                        </linearGradient>
                      </defs>
                    </svg>
                    {ScreenType.PREMIUM}
                  </th>
                  <th className={`${thClass} `}>{ScreenType.BASIC}</th>
                  <th className={thClass}>Assigned to</th>
                  <th className={thClass}>Billing Date</th>
                  <th className={thClass}>End Date</th>
                  <th className={thClass}>Total ₹</th>
                  <th className={thClass}></th>
                </tr>
              </thead>

              <tbody>
                {visibleSummaries.map((summary: any) => (
                  <React.Fragment key={summary.id}>
                    <tr
                      className={`cursor-pointer ${
                        theme === "dark" ? "bg-#222222 " : "bg-gray-50"
                      }`}
                      onClick={() => {
                        if (summary.status !== "Pending") toggleRow(summary.id);
                      }}
                      style={
                        summary.status === "Pending"
                          ? { opacity: 0.6, cursor: "not-allowed" }
                          : undefined
                      }
                    >
                      <td className={tdClass}>{summary.totalScreens}</td>
                      <td className={tdClass}>{summary.premium}</td>
                      <td className={tdClass}>{summary.basic}</td>
                      <td className={tdClass}>{summary.organisation.name}</td>
                      <td className={tdClass}>{summary.billingDate}</td>
                      <td className={tdClass}>
                        {summary.endDate ? summary.endDate : "--"}
                      </td>
                      <td className={tdClass}>{summary.total}</td>
                      <td className={tdClass}>
                        <div className="flex items-center justify-around">
                          <div
                            title={
                              summary.status === "Pending"
                                ? "Payment is pending"
                                : undefined
                            }
                            style={
                              summary.status === "Pending"
                                ? { opacity: 1, cursor: "allowed" }
                                : undefined
                            }
                          >
                            <svg
                              className={`w-4 h-4 transition-transform ${
                                expandedRows.has(summary.id) ? "rotate-180" : ""
                              }`}
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Detail Rows */}
                    {expandedRows.has(summary.id) &&
                      summary.status !== "Pending" && (
                        <tr>
                          <td className="px-0 py-0 border-0" colSpan={8}>
                            <div className="mx-6 mb-4 mt-2">
                              {/* Expanded Table Container with Border */}
                              <div
                                className={`rounded-lg border-2 overflow-hidden ${
                                  theme === "dark"
                                    ? "border-[#2A2A2A]"
                                    : "border-gray-200"
                                }`}
                              >
                                <table className="min-w-full">
                                  {/* Table Header with Different Background */}
                                  <thead
                                    className={
                                      theme === "dark"
                                        ? "bg-[#2A2A2A]"
                                        : "bg-gray-100"
                                    }
                                  >
                                    <tr>
                                      <th
                                        className={`px-4 py-3 text-left text-sm font-bold  tracking-wider ${
                                          theme === "dark"
                                            ? "text-gray-300"
                                            : "text-gray-700"
                                        }`}
                                      >
                                        ID
                                      </th>
                                      <th
                                        className={`px-4 py-3 text-left text-sm font-bold  tracking-wider ${
                                          theme === "dark"
                                            ? "text-gray-300"
                                            : "text-gray-700 "
                                        }`}
                                      >
                                        Screen Type
                                      </th>
                                      <th
                                        className={`px-4 py-3 text-left text-sm font-bold  tracking-wider ${
                                          theme === "dark"
                                            ? "text-gray-300"
                                            : "text-gray-700"
                                        }`}
                                      >
                                        Gym
                                      </th>
                                      <th
                                        className={`px-4 py-3 text-left text-sm font-bold  tracking-wider ${
                                          theme === "dark"
                                            ? "text-gray-300"
                                            : "text-gray-700"
                                        }`}
                                      >
                                        Location
                                      </th>
                                      <th
                                        className={`px-4 py-3 text-left text-sm font-bold  tracking-wider ${
                                          theme === "dark"
                                            ? "text-gray-300"
                                            : "text-gray-700"
                                        }`}
                                      >
                                        Status
                                      </th>

                                      <th
                                        className={`px-4 py-3 text-left text-sm font-bold  tracking-wider ${
                                          theme === "dark"
                                            ? "text-gray-300"
                                            : "text-gray-700"
                                        }`}
                                      >
                                        Action
                                      </th>
                                    </tr>
                                  </thead>
                                  {/* Table Body with Original Background */}
                                  <tbody
                                    className={
                                      theme === "dark"
                                        ? "bg-[#1A1A1A] divide-y divide-[#2A2A2A]"
                                        : "bg-white divide-y divide-gray-200"
                                    }
                                  >
                                    {/* Filter screens by subscriber === summary.id */}
                                    {(screens ?? [])
                                      .filter(
                                        (screen: ScreenData) =>
                                          screen.subscription === summary.id
                                      )
                                      .map(
                                        (screen: ScreenData, index: number) => {
                                          let statusText = screen.status;
                                          let statusNode: React.ReactNode =
                                            statusText;

                                          if (
                                            screen.cancel &&
                                            screen.cancelledAt
                                          ) {
                                            // Cancelled: "Expired at" + date, gray color
                                            statusNode = (
                                              <span>
                                                Expired at{" "}
                                                <span
                                                  className={
                                                    theme === "dark"
                                                      ? "text-gray-300"
                                                      : "text-gray-700"
                                                  }
                                                >
                                                  {new Date(
                                                    screen.endDate
                                                  ).toLocaleDateString()}
                                                </span>
                                              </span>
                                            );
                                          } else if (
                                            screen.downgrade &&
                                            screen.downgradedAt
                                          ) {
                                            statusNode = (
                                              <span>
                                                Expiring on{" "}
                                                <span className="text-[#B6CB24]">
                                                  {new Date(
                                                    screen.downgradedAt
                                                  ).toLocaleDateString()}
                                                </span>
                                              </span>
                                            );
                                          }

                                          // Icon logic
                                          const showDeleteIcon = !screen.cancel;
                                          const showResubscribeIcon =
                                            !!screen.cancel;

                                          return (
                                            <tr
                                              key={`${screen._id}-${index}`}
                                              className={
                                                theme === "dark"
                                                  ? "hover:bg-[#222222]"
                                                  : "hover:bg-gray-50"
                                              }
                                            >
                                              <td
                                                className={`px-4 py-3 text-sm ${
                                                  theme === "dark"
                                                    ? "text-gray-300"
                                                    : "text-gray-900"
                                                }`}
                                              >
                                                {screen.code}
                                              </td>
                                              <td
                                                className={`px-4 py-3 text-sm ${
                                                  theme === "dark"
                                                    ? "text-gray-300"
                                                    : "text-gray-900"
                                                }`}
                                              >
                                                <div className="flex items-center gap-2">
                                                  {screen.plan.type ===
                                                  ScreenType.PREMIUM ? (
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      width="24"
                                                      height="24"
                                                      viewBox="0 0 24 24"
                                                      fill="none"
                                                    >
                                                      <circle
                                                        cx="12.75"
                                                        cy="12.75"
                                                        r="10.75"
                                                        fill="url(#paint0_radial_278_5485)"
                                                      />
                                                      <g filter="url(#filter0_n_278_5485)">
                                                        <path
                                                          fill-rule="evenodd"
                                                          clip-rule="evenodd"
                                                          d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                                                          fill="url(#paint1_linear_278_5485)"
                                                        />
                                                        <path
                                                          fill-rule="evenodd"
                                                          clip-rule="evenodd"
                                                          d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2ZM13.9312 7.69832C13.4315 6.76723 12.0677 6.76722 11.568 7.69832L10.332 10.0014L7.63056 10.4099C6.59836 10.566 6.07502 11.8457 6.90365 12.6346L8.81505 14.4546L8.39536 16.9848C8.20793 18.1148 9.40827 18.7971 10.3045 18.3635L12.7496 17.1807L15.1947 18.3635C16.0909 18.7971 17.2913 18.1148 17.1038 16.9848L16.6841 14.4546L18.5955 12.6346C19.4242 11.8457 18.9008 10.566 17.8686 10.4099L15.1671 10.0014L13.9312 7.69832Z"
                                                          fill="#C6D92C"
                                                          fillOpacity="0.1"
                                                        />
                                                      </g>
                                                      <circle
                                                        cx="12.75"
                                                        cy="12.75"
                                                        r="10.25"
                                                        stroke="url(#paint2_linear_278_5485)"
                                                        stroke-opacity="0.88"
                                                      />
                                                      <path
                                                        d="M11.2939 7.81641C11.6993 7.06107 12.8114 7.06112 13.2168 7.81641L14.4531 10.1191L14.5117 10.2295L14.6357 10.249L17.3369 10.6572C18.1918 10.7865 18.5864 11.8269 17.9287 12.4531L16.0176 14.2734L15.9219 14.3652L15.9434 14.4951L16.3633 17.0254C16.4394 17.4839 16.238 17.8433 15.9238 18.0518C15.6023 18.2649 15.1713 18.3136 14.8096 18.1387L12.3643 16.9561L12.2559 16.9033L12.1465 16.9561L9.70117 18.1387C9.33945 18.3135 8.90938 18.2648 8.58789 18.0518C8.27342 17.8433 8.07137 17.4841 8.14746 17.0254L8.56738 14.4951L8.58887 14.3652L8.49316 14.2734L6.58203 12.4531C5.92445 11.8269 6.31904 10.7865 7.17383 10.6572L9.875 10.249L9.99902 10.2295L10.0586 10.1191L11.2939 7.81641Z"
                                                        stroke="url(#paint3_linear_278_5485)"
                                                        stroke-opacity="0.88"
                                                        strokeWidth="0.5"
                                                      />
                                                      <defs>
                                                        <filter
                                                          id="filter0_n_278_5485"
                                                          x="2"
                                                          y="2"
                                                          width="21.5"
                                                          height="21.5"
                                                          filterUnits="userSpaceOnUse"
                                                          color-interpolation-filters="sRGB"
                                                        >
                                                          <feFlood
                                                            flood-opacity="0"
                                                            result="BackgroundImageFix"
                                                          />
                                                          <feBlend
                                                            mode="normal"
                                                            in="SourceGraphic"
                                                            in2="BackgroundImageFix"
                                                            result="shape"
                                                          />
                                                          <feTurbulence
                                                            type="fractalNoise"
                                                            baseFrequency="2 2"
                                                            stitchTiles="stitch"
                                                            numOctaves="3"
                                                            result="noise"
                                                            seed="2744"
                                                          />
                                                          <feColorMatrix
                                                            in="noise"
                                                            type="luminanceToAlpha"
                                                            result="alphaNoise"
                                                          />
                                                          <feComponentTransfer
                                                            in="alphaNoise"
                                                            result="coloredNoise1"
                                                          >
                                                            <feFuncA
                                                              type="discrete"
                                                              tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
                                                            />
                                                          </feComponentTransfer>
                                                          <feComposite
                                                            operator="in"
                                                            in2="shape"
                                                            in="coloredNoise1"
                                                            result="noise1Clipped"
                                                          />
                                                          <feFlood
                                                            flood-color="rgba(0, 0, 0, 0.25)"
                                                            result="color1Flood"
                                                          />
                                                          <feComposite
                                                            operator="in"
                                                            in2="noise1Clipped"
                                                            in="color1Flood"
                                                            result="color1"
                                                          />
                                                          <feMerge result="effect1_noise_278_5485">
                                                            <feMergeNode in="shape" />
                                                            <feMergeNode in="color1" />
                                                          </feMerge>
                                                        </filter>
                                                        <radialGradient
                                                          id="paint0_radial_278_5485"
                                                          cx="0"
                                                          cy="0"
                                                          r="1"
                                                          gradientUnits="userSpaceOnUse"
                                                          gradientTransform="translate(10.5 10) rotate(54.7824) scale(10.4043)"
                                                        >
                                                          <stop stopColor="#D1E156" />
                                                          <stop
                                                            offset="0.552885"
                                                            stopColor="#C6D92C"
                                                          />
                                                          <stop
                                                            offset="1"
                                                            stopColor="#7A8618"
                                                          />
                                                        </radialGradient>
                                                        <linearGradient
                                                          id="paint1_linear_278_5485"
                                                          x1="12.75"
                                                          y1="3"
                                                          x2="12.75"
                                                          y2="22.5"
                                                          gradientUnits="userSpaceOnUse"
                                                        >
                                                          <stop stopColor="#2E2E2E" />
                                                          <stop
                                                            offset="1"
                                                            stopColor="#666666"
                                                          />
                                                        </linearGradient>
                                                        <linearGradient
                                                          id="paint2_linear_278_5485"
                                                          x1="2"
                                                          y1="12.75"
                                                          x2="23.5"
                                                          y2="12.75"
                                                          gradientUnits="userSpaceOnUse"
                                                        >
                                                          <stop stopColor="#C6D92C" />
                                                          <stop
                                                            offset="0.533654"
                                                            stopColor="#EDF5C0"
                                                          />
                                                          <stop
                                                            offset="1"
                                                            stopColor="#C6D92C"
                                                          />
                                                        </linearGradient>
                                                        <linearGradient
                                                          id="paint3_linear_278_5485"
                                                          x1="6"
                                                          y1="12.7476"
                                                          x2="18.5109"
                                                          y2="12.7476"
                                                          gradientUnits="userSpaceOnUse"
                                                        >
                                                          <stop stopColor="#C6D92C" />
                                                          <stop
                                                            offset="0.533654"
                                                            stopColor="#EDF5C0"
                                                          />
                                                          <stop
                                                            offset="1"
                                                            stopColor="#C6D92C"
                                                          />
                                                        </linearGradient>
                                                      </defs>
                                                    </svg>
                                                  ) : (
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      width="24"
                                                      height="24"
                                                      viewBox="0 0 24 24"
                                                      fill="none"
                                                    >
                                                      <g filter="url(#filter0_n_1471_2452)">
                                                        <path
                                                          fill-rule="evenodd"
                                                          clip-rule="evenodd"
                                                          d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2Z"
                                                          fill="url(#paint0_linear_1471_2452)"
                                                        />
                                                        <path
                                                          fill-rule="evenodd"
                                                          clip-rule="evenodd"
                                                          d="M12.75 2C6.81294 2 2 6.81294 2 12.75C2 18.687 6.81293 23.5 12.75 23.5C18.687 23.5 23.5 18.687 23.5 12.75C23.5 6.81293 18.687 2 12.75 2Z"
                                                          fill="#C6D92C"
                                                          fillOpacity="0.1"
                                                        />
                                                      </g>
                                                      <circle
                                                        cx="12.75"
                                                        cy="12.75"
                                                        r="10.25"
                                                        stroke="url(#paint1_linear_1471_2452)"
                                                        stroke-opacity="0.88"
                                                      />
                                                      <defs>
                                                        <filter
                                                          id="filter0_n_1471_2452"
                                                          x="2"
                                                          y="2"
                                                          width="21.5"
                                                          height="21.5"
                                                          filterUnits="userSpaceOnUse"
                                                          color-interpolation-filters="sRGB"
                                                        >
                                                          <feFlood
                                                            flood-opacity="0"
                                                            result="BackgroundImageFix"
                                                          />
                                                          <feBlend
                                                            mode="normal"
                                                            in="SourceGraphic"
                                                            in2="BackgroundImageFix"
                                                            result="shape"
                                                          />
                                                          <feTurbulence
                                                            type="fractalNoise"
                                                            baseFrequency="2 2"
                                                            stitchTiles="stitch"
                                                            numOctaves="3"
                                                            result="noise"
                                                            seed="2744"
                                                          />
                                                          <feColorMatrix
                                                            in="noise"
                                                            type="luminanceToAlpha"
                                                            result="alphaNoise"
                                                          />
                                                          <feComponentTransfer
                                                            in="alphaNoise"
                                                            result="coloredNoise1"
                                                          >
                                                            <feFuncA
                                                              type="discrete"
                                                              tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
                                                            />
                                                          </feComponentTransfer>
                                                          <feComposite
                                                            operator="in"
                                                            in2="shape"
                                                            in="coloredNoise1"
                                                            result="noise1Clipped"
                                                          />
                                                          <feFlood
                                                            flood-color="rgba(0, 0, 0, 0.25)"
                                                            result="color1Flood"
                                                          />
                                                          <feComposite
                                                            operator="in"
                                                            in2="noise1Clipped"
                                                            in="color1Flood"
                                                            result="color1"
                                                          />
                                                          <feMerge result="effect1_noise_1471_2452">
                                                            <feMergeNode in="shape" />
                                                            <feMergeNode in="color1" />
                                                          </feMerge>
                                                        </filter>
                                                        <linearGradient
                                                          id="paint0_linear_1471_2452"
                                                          x1="12.75"
                                                          y1="3"
                                                          x2="12.75"
                                                          y2="22.5"
                                                          gradientUnits="userSpaceOnUse"
                                                        >
                                                          <stop stopColor="#2E2E2E" />
                                                          <stop
                                                            offset="1"
                                                            stopColor="#666666"
                                                          />
                                                        </linearGradient>
                                                        <linearGradient
                                                          id="paint1_linear_1471_2452"
                                                          x1="2"
                                                          y1="12.75"
                                                          x2="23.5"
                                                          y2="12.75"
                                                          gradientUnits="userSpaceOnUse"
                                                        >
                                                          <stop stopColor="#C6D92C" />
                                                          <stop
                                                            offset="0.533654"
                                                            stopColor="#EDF5C0"
                                                          />
                                                          <stop
                                                            offset="1"
                                                            stopColor="#C6D92C"
                                                          />
                                                        </linearGradient>
                                                      </defs>
                                                    </svg>
                                                  )}
                                                </div>
                                              </td>

                                              <td
                                                className={`px-4 py-3 text-sm ${
                                                  theme === "dark"
                                                    ? "text-gray-300"
                                                    : "text-gray-900"
                                                }`}
                                              >
                                                {/* Gym column, always show -- */}
                                                --
                                              </td>
                                              <td
                                                className={`px-4 py-3 text-sm ${
                                                  theme === "dark"
                                                    ? "text-gray-300"
                                                    : "text-gray-900"
                                                }`}
                                              >
                                                {/* Gym column, always show -- */}
                                                --
                                              </td>

                                              <td
                                                className={`px-4 py-3 text-sm ${
                                                  theme === "dark"
                                                    ? "text-gray-300"
                                                    : "text-gray-900"
                                                }`}
                                              >
                                                {screen.cancel &&
                                                screen.endDate ? (
                                                  <span>
                                                    Expired at{" "}
                                                    <span
                                                      className={
                                                        theme === "dark"
                                                          ? "text-gray-300"
                                                          : "text-gray-700"
                                                      }
                                                    >
                                                      {new Date(
                                                        screen.endDate
                                                      ).toLocaleDateString()}
                                                    </span>
                                                  </span>
                                                ) : screen.downgrade &&
                                                  screen.downgradedAt ? (
                                                  <span>
                                                    Expiring on{" "}
                                                    <span className="text-[#B6CB24]">
                                                      {new Date(
                                                        screen.downgradedAt
                                                      ).toLocaleDateString()}
                                                    </span>
                                                  </span>
                                                ) : (
                                                  <span className="text-[#B6CB24]">
                                                    {screen.status}
                                                  </span>
                                                )}
                                              </td>
                                              <td
                                                className={`px-4 py-3 text-sm ${
                                                  theme === "dark"
                                                    ? "text-gray-300"
                                                    : "text-gray-900"
                                                }`}
                                              >
                                                <div className="flex items-center gap-2">
                                                  <button
                                                    className="text-sm font-medium flex items-center gap-1 px-3 py-1"
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: "8px",
                                                      padding: "8px 16px",
                                                      borderRadius: "10px",
                                                      border:
                                                        screen.cancel ||
                                                        screen.plan.type ===
                                                          "NORMAL"
                                                          ? theme === "dark"
                                                            ? "1px solid #C6D92C"
                                                            : "1px solid #6D790F"
                                                          : theme === "dark"
                                                          ? "1px solid rgba(250, 252, 237, 0.30)"
                                                          : "1px solid rgba(109,121,15,0.30)",
                                                      background:
                                                        screen.cancel ||
                                                        screen.plan.type ===
                                                          "NORMAL"
                                                          ? theme === "dark"
                                                            ? "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198,217,44,0.40) 0%, rgba(83,91,16,0.40) 100%)"
                                                            : "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198,217,44,0.15) 0%, rgba(83,91,16,0.15) 100%)"
                                                          : "transparent",
                                                      color:
                                                        theme === "dark"
                                                          ? "#FFFFFF"
                                                          : "#1A1A1A",
                                                      fontWeight: 600,
                                                      opacity:
                                                        screen.isDowngradeRequested
                                                          ? 0.5
                                                          : 1,
                                                      cursor:
                                                        screen.isDowngradeRequested
                                                          ? "not-allowed"
                                                          : "pointer",
                                                    }}
                                                    disabled={
                                                      screen.isDowngradeRequested
                                                    }
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      if (
                                                        screen.isDowngradeRequested
                                                      )
                                                        return;

                                                      let actionText = "";
                                                      if (screen.cancel) {
                                                        actionText =
                                                          "Resubscribe";
                                                      } else if (
                                                        screen.downgrade
                                                      ) {
                                                        actionText =
                                                          "Downgrading";
                                                      } else {
                                                        actionText =
                                                          screen.plan.type ===
                                                          "NORMAL"
                                                            ? "Upgrade"
                                                            : "Downgrade";
                                                      }
                                                      // compute targetPlanId: find a screen in same subscription with the opposite plan type
                                                      const currentPlanId =
                                                        screen.plan?._id;
                                                      const currentType =
                                                        screen.plan?.type;
                                                      const targetType =
                                                        currentType === "NORMAL"
                                                          ? "PREMIUM"
                                                          : "NORMAL";
                                                      const targetScreen = (
                                                        screens ?? []
                                                      ).find(
                                                        (s) =>
                                                          s.subscription ===
                                                            screen.subscription &&
                                                          s.plan?.type ===
                                                            targetType
                                                      );
                                                      const targetPlanId =
                                                        targetScreen?.plan
                                                          ?._id ??
                                                        currentPlanId;

                                                      const screenRow: ScreenRow =
                                                        {
                                                          id: screen._id,
                                                          screen:
                                                            screen.plan.type,
                                                          gym: "--",
                                                          location: "--",
                                                          action: actionText,
                                                          screenId: screen._id,
                                                          currentPlanId:
                                                            currentPlanId,
                                                          planId: currentPlanId,
                                                          targetPlanId:
                                                            targetPlanId,
                                                          // send billing and end date so UpgradeModal can show them
                                                          billingDate:
                                                            summary.billingDate,
                                                          endDate:
                                                            summary.endDate,
                                                        };
                                                      if (screen.cancel) {
                                                        // handle resubscribe if needed
                                                      } else if (
                                                        screen.plan.type ===
                                                        "BASIC"
                                                      ) {
                                                        openUpgradeModal(
                                                          screenRow
                                                        );
                                                      } else {
                                                        openDowngradeModal(
                                                          screenRow
                                                        );
                                                      }
                                                    }}
                                                  >
                                                    {/* Icon logic */}
                                                    {showResubscribeIcon ? (
                                                      <ResubscribeIcon
                                                        theme={
                                                          theme as
                                                            | "light"
                                                            | "dark"
                                                        }
                                                      />
                                                    ) : screen.downgrade ||
                                                      (!screen.cancel &&
                                                        screen.plan.type ===
                                                          ScreenType.PREMIUM) ? (
                                                      <DowngradeIcon
                                                        theme={
                                                          theme as
                                                            | "light"
                                                            | "dark"
                                                        }
                                                      />
                                                    ) : screen.plan.type ===
                                                      ScreenType.BASIC ? (
                                                      <UpgradeIcon
                                                        theme={
                                                          theme as
                                                            | "light"
                                                            | "dark"
                                                        }
                                                      />
                                                    ) : (
                                                      <UpgradeIcon
                                                        theme={
                                                          theme as
                                                            | "light"
                                                            | "dark"
                                                        }
                                                      />
                                                    )}
                                                    {screen.cancel
                                                      ? "Resubscribe"
                                                      : screen.downgrade ||
                                                        screen.isDowngradeRequested
                                                      ? "Downgrading"
                                                      : screen.plan.type ===
                                                        ScreenType.BASIC
                                                      ? "Upgrade"
                                                      : "Downgrade"}
                                                  </button>
                                                  {showDeleteIcon && (
                                                    <button
                                                      title="Delete"
                                                      className={`p-1.5 rounded transition-colors ${iconHover}`}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        const screenRow: ScreenRow =
                                                          {
                                                            id: screen._id,
                                                            screen:
                                                              screen.plan.type,
                                                            gym: "--",
                                                            location: "--",
                                                            action: "Delete",
                                                            screenId:
                                                              screen._id,
                                                            planId:
                                                              screen.plan._id,
                                                            billingDate:
                                                              summary.billingDate,
                                                            endDate:
                                                              summary.endDate,
                                                          };
                                                        openDelete(screenRow);
                                                      }}
                                                    >
                                                      <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                      >
                                                        <path
                                                          d="M12.0342 1.25C12.5408 1.25 12.9701 1.24949 13.3252 1.28125C13.694 1.31425 14.038 1.38536 14.3721 1.55859C14.5041 1.62709 14.6306 1.7068 14.75 1.7959C15.0518 2.02101 15.2663 2.29951 15.457 2.61719C15.6405 2.92284 15.8269 3.3089 16.0479 3.76465L16.5254 4.75H21C21.4142 4.75 21.75 5.08579 21.75 5.5C21.75 5.91421 21.4142 6.25 21 6.25H20.2051L19.6289 15.5713C19.5506 16.8374 19.4891 17.8405 19.3623 18.6396C19.2336 19.4506 19.0281 20.126 18.6162 20.7168C18.2393 21.2572 17.7534 21.7138 17.1904 22.0566C16.5754 22.4312 15.8887 22.5955 15.0713 22.6738C14.2659 22.751 13.2615 22.75 11.9932 22.75C10.723 22.75 9.71658 22.7512 8.91016 22.6738C8.09168 22.5953 7.40366 22.4311 6.78809 22.0557C6.22491 21.7122 5.74005 21.2543 5.36328 20.7129C4.9515 20.1211 4.74587 19.4449 4.61816 18.6328C4.49235 17.8326 4.43298 16.8283 4.35645 15.5605L3.79395 6.25H3C2.58579 6.25 2.25 5.91421 2.25 5.5C2.25 5.08579 2.58579 4.75 3 4.75H7.57031L7.97656 3.86035C8.19193 3.38787 8.37299 2.98768 8.55371 2.6709C8.7415 2.34175 8.95531 2.05271 9.26074 1.81836C9.38127 1.7259 9.50945 1.64359 9.64355 1.57227L9.77148 1.50879C10.0717 1.37296 10.3826 1.3123 10.7129 1.28223C11.076 1.24919 11.5151 1.25 12.0342 1.25ZM5.29688 6.25L5.85352 15.4697C5.93181 16.7666 5.98757 17.6867 6.09961 18.3994C6.20977 19.1001 6.36482 19.5261 6.59473 19.8564C6.85249 20.2268 7.18424 20.5395 7.56934 20.7744C7.91297 20.984 8.34764 21.1129 9.05371 21.1807C9.77189 21.2496 10.6939 21.25 11.9932 21.25C13.2905 21.25 14.2106 21.2494 14.9277 21.1807C15.6328 21.1131 16.0668 20.9845 16.4102 20.7754C16.7469 20.5703 17.0435 20.3058 17.2852 19.9951L17.3857 19.8584C17.6156 19.5286 17.7699  19.1036 17.8809 18.4043C17.9937 17.6926 18.0517 16.7737 18.1318 15.4785L18.7021 6.25H5.29688ZM12.0342 2.75C11.4881 2.75 11.1282 2.75092 10.8486 2.77637C10.5832 2.80053 10.448 2.84313 10.3477 2.89648C10.2868 2.92885 10.2285 2.96588 10.1738 3.00781C10.0837 3.07697 9.98855 3.18253 9.85645 3.41406C9.71731 3.65796 9.56738 3.9854 9.34082 4.48242L9.21875 4.75H14.8584L14.6982 4.41895C14.4657 3.93934 14.312 3.62365 14.1709 3.38867C14.0372 3.16597 13.9427 3.06462 13.8535 2.99805C13.8264 2.97784 13.7982 2.95839 13.7695 2.94043L13.6816 2.89062C13.5828 2.83935 13.4504 2.79856 13.1914 2.77539C12.9184 2.75098 12.5672 2.75 12.0342 2.75Z"
                                                          fill="url(#paint0_linear_278_5502)"
                                                        />
                                                        <path
                                                          d="M9.5 16.5V10.5"
                                                          stroke="url(#paint1_linear_278_5502)"
                                                          strokeWidth="1.5"
                                                          strokeLinecap="round"
                                                        />
                                                        <path
                                                          d="M14.5 16.5V10.5"
                                                          stroke="url(#paint2_linear_278_5502)"
                                                          strokeWidth="1.5"
                                                          strokeLinecap="round"
                                                        />
                                                        <defs>
                                                          <linearGradient
                                                            id="paint0_linear_278_5502"
                                                            x1="12"
                                                            y1="1.25"
                                                            x2="12"
                                                            y2="22.75"
                                                            gradientUnits="userSpaceOnUse"
                                                          >
                                                            <stop stopColor="#D92626" />
                                                            <stop
                                                              offset="1"
                                                              stopColor="#AD1F1F"
                                                            />
                                                          </linearGradient>
                                                          <linearGradient
                                                            id="paint1_linear_278_5502"
                                                            x1="10"
                                                            y1="10.5"
                                                            x2="10"
                                                            y2="16.5"
                                                            gradientUnits="userSpaceOnUse"
                                                          >
                                                            <stop stopColor="#D92626" />
                                                            <stop
                                                              offset="1"
                                                              stopColor="#AD1F1F"
                                                            />
                                                          </linearGradient>
                                                          <linearGradient
                                                            id="paint2_linear_278_5502"
                                                            x1="15"
                                                            y1="10.5"
                                                            x2="15"
                                                            y2="16.5"
                                                            gradientUnits="userSpaceOnUse"
                                                          >
                                                            <stop stopColor="#D92626" />
                                                            <stop
                                                              offset="1"
                                                              stopColor="#AD1F1F"
                                                            />
                                                          </linearGradient>
                                                        </defs>
                                                      </svg>
                                                    </button>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        }
                                      )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AddScreenModal
        isOpen={showAddScreenModal}
        onClose={closeAddScreenModal}
        onSuccess={handleScreenAdded}
      />

      <ConfirmActionModal
        isOpen={!!deleteTarget}
        onClose={closeDelete}
        onConfirm={confirmDelete}
        title="Delete Screen"
        message={`Are you sure you want to delete screen ?`}
        confirmText="Confirm and delete"
        type="delete"
      />

      <ConfirmActionModal
        isOpen={showDowngradeModal && selectedScreen?.action === "Downgrade"}
        onClose={closeDowngradeModal}
        onConfirm={confirmDowngrade}
        title={`${selectedScreen?.action} Screen`}
        message={`Are you sure you want to ${selectedScreen?.action} the screen ? `}
        confirmText={selectedScreen?.action || "Confirm"}
        type="action"
      />

      <UpgradeModal
        isOpen={showUpgradeModal && selectedScreen?.action === "Upgrade"}
        onClose={closeUpgradeModal}
        onConfirm={confirmUpgrade}
        selectedScreen={selectedScreen}
      />

      <ComparePlansModal
        isOpen={showComparePlansModal}
        onClose={closeComparePlansModal}
      />
    </div>
  );
};

export default ScreenList;
